<template>
  <div class="all-courses-page">
    <Navbar />
    <PageBanner :title="t('navbar.workshopCourses')" />
    <Breadcrumb :items="breadcrumbItems" />

    <div class="content">
      <div class="find-container">
        <h2 class="find-title">{{ t("findCourse.courseCatelog") }}</h2>

        <div class="filter-row desktop-filters">
          <div class="filter-group">
            <div class="custom-select-wrap">
              <span class="select-label">分類</span>
              <span class="select-divider">|</span>
              <select v-model="filterType" class="select-input" aria-label="篩選課程分類" title="篩選課程分類">
                <option value="all">全部</option>
                <option
                  v-for="cat in categories"
                  :key="cat.id"
                  :value="String(cat.id)"
                >
                  {{ cat.name }}
                </option>
              </select>
              <span class="select-arrow" aria-hidden="true">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
          <div class="filter-group">
            <div class="custom-select-wrap">
              <span class="select-label">標籤</span>
              <span class="select-divider">|</span>
              <select v-model="tagType" class="select-input" aria-label="篩選課程標籤" title="篩選課程標籤">
                <option value="all">全部</option>
                <option
                  v-for="tag in tags"
                  :key="tag.id"
                  :value="String(tag.id)"
                >
                  {{ tag.name }}
                </option>
              </select>
              <span class="select-arrow" aria-hidden="true">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
          <div class="filter-group sort-group">
            <div class="custom-select-wrap">
              <span class="select-label">排序</span>
              <span class="select-divider">|</span>
              <select v-model="sortType" class="select-input" aria-label="課程排序方式" title="課程排序方式">
                <option value="latest">最新</option>
                <option value="oldest">最舊</option>
              </select>
              <span class="select-arrow" aria-hidden="true">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#1A1A1A" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <!-- 課程卡片網格 -->
        <div class="course-grid" v-if="!loading && courses.length > 0">
          <div
            v-for="course in courses"
            :key="course.id"
            class="course-card"
            @click="goToCourse(course.id)"
          >
            <div class="course-image-container">
              <div :class="['status-tag', getCourseStatus(course)]">
                {{ getStatusLabel(getCourseStatus(course), false) }}
              </div>
              <img
                v-if="course.featured_image_url"
                :src="course.featured_image_url"
                :alt="course.title"
                class="course-image"
              />
              <div v-else class="course-image-placeholder"></div>
            </div>
            <div class="course-info">
              <div class="course-title-row">
                <h3 class="course-title">{{ course.title }}</h3>
                <button
                  type="button"
                  class="favorite-icon-button"
                  :class="{ active: course.is_favorited }"
                  @click.stop="toggleFavorite(course)"
                  :aria-label="course.is_favorited ? `取消收藏課程：${course.title}` : `收藏課程：${course.title}`"
                  :title="course.is_favorited ? '取消收藏' : '加入收藏'"
                >
                  <svg
                    width="14"
                    height="18"
                    viewBox="0 0 14 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M0 18V2C0 1.45 0.196 0.979333 0.588 0.588C0.98 0.196667 1.45067 0.000666667 2 0H12C12.55 0 13.021 0.196 13.413 0.588C13.805 0.98 14.0007 1.45067 14 2V18L7 15L0 18Z"
                      :fill="course.is_favorited ? '#1171DD' : 'none'"
                      stroke="#1171DD"
                      stroke-width="2"
                    />
                  </svg>
                </button>
              </div>
              <div class="course-meta">
                <span
                  class="meta-item tag-item"
                  v-if="course.categories && course.categories.length"
                >
                  {{ course.categories[0].name }}
                </span>
                <span v-if="course.tags && course.tags.length" class="tag-item">
                  {{ course.tags[0].name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 空狀態 -->
        <div v-if="!loading && courses.length === 0" class="empty-state">
          目前沒有符合的課程
        </div>
      </div>
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { msCourseAPI } from "@/services/api.js";

const { t } = useI18n();
const router = useRouter();

const getCourseStatus = (course) => {
  if (course.status_override) {
    return course.status_override;
  }
  const now = new Date();
  const regStart = course.registration_start_at
    ? new Date(course.registration_start_at.replace(/-/g, "/"))
    : null;
  const regEnd = course.registration_end_at
    ? new Date(course.registration_end_at.replace(/-/g, "/"))
    : null;
  const classEnd = course.class_end_at
    ? new Date(course.class_end_at.replace(/-/g, "/"))
    : null;

  if (!regStart || !regEnd || !classEnd) {
    return "not_started";
  }

  if (now < regStart) {
    return "not_started";
  } else if (now >= regStart && now <= regEnd) {
    return "open";
  } else if (now > regEnd && now < classEnd) {
    return "closed";
  } else {
    return "ended";
  }
};

const getStatusLabel = (status, isDetail) => {
  if (isDetail) {
    switch (status) {
      case "not_started":
        return "尚未開放報名";
      case "open":
        return "報名中";
      case "closed":
        return "報名已截止";
      case "ended":
        return "課程已結束";
      default:
        return "";
    }
  } else {
    switch (status) {
      case "not_started":
        return "即將開放";
      case "open":
        return "報名中";
      case "closed":
        return "報名截止";
      case "ended":
        return "已結束";
      default:
        return "";
    }
  }
};

import Navbar from "@/components/Navbar.vue";
import FooterSection from "@/components/FooterSection.vue";
import PageBanner from "@/components/PageBanner.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";

const filterType = ref("all");
const tagType = ref("all");
const sortType = ref("latest");
const loading = ref(false);
const courses = ref([]);
const categories = ref([]);
const tags = ref([]);

const breadcrumbItems = [
  { label: t("navbar.home"), path: "/" },
  { label: t("navbar.training") },
  { label: t("navbar.workshopCourses") },
];

const fetchFilters = async () => {
  const [catRes, tagRes] = await Promise.all([
    msCourseAPI.listCategories(),
    msCourseAPI.listTags(),
  ]);
  categories.value = Array.isArray(catRes.data) ? catRes.data : [];
  tags.value = Array.isArray(tagRes.data) ? tagRes.data : [];
};

const fetchCourses = async () => {
  loading.value = true;
  try {
    const params = {
      per_page: 50,
      sort: sortType.value === "latest" ? "publish_at_desc" : "title_asc",
    };
    if (filterType.value !== "all") {
      params["category_ids[]"] = filterType.value;
    }
    if (tagType.value !== "all") {
      params["tag_ids[]"] = tagType.value;
    }

    const res = await msCourseAPI.listCourses(params);
    courses.value = (res?.data?.data || []).map((course) => ({
      ...course,
      is_favorited: course.is_favorited === true,
    }));
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchFilters();
  await fetchCourses();
});

const goToCourse = (id) => {
  router.push(`/course/${id}`);
};

const isLoggedIn = () => !!localStorage.getItem("ms_platform_token");

const toggleFavorite = async (course) => {
  if (!isLoggedIn()) {
    router.push("/sign-in");
    return;
  }

  try {
    if (course.is_favorited) {
      await msCourseAPI.removeFavorite(course.id);
      course.is_favorited = false;
      message.success(t("favoriteList.removedFromFavorites"));
      return;
    }

    await msCourseAPI.addFavorite(course.id);
    course.is_favorited = true;
    message.success(t("courseDetail.favorited"));
  } catch (error) {
    message.error(t("findCourse.getFavoriteCoursesFailed"));
  }
};

watch([filterType, tagType, sortType], () => {
  fetchCourses();
});
</script>

<style scoped lang="scss">
.all-courses-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.find-container {
  margin-top: 20px;
}

.find-title {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #333333;
  margin-bottom: 24px;
  line-height: 1.4; /* 140% */
  letter-spacing: 0;
}

.filter-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  justify-content: space-between; /* 使類型靠左，排序靠右 */
}

.filter-group {
  display: flex;
  align-items: center;
}

.custom-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 140px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #b4b4b4;
  border-radius: 6px;
  padding: 0 12px;
  box-sizing: border-box;

  .select-label {
    font-size: 16px !important;
    color: #1a1a1a;
    white-space: nowrap;
    user-select: none;
  }

  .select-divider {
    margin: 0 8px;
    color: #b4b4b4;
    font-size: 16px !important;
    user-select: none;
  }

  select.select-input {
    flex: 1;
    height: 100%;
    border: none;
    background: transparent !important;
    outline: none;
    font-size: 16px !important;
    font-family: inherit !important;
    color: #1a1a1a !important;
    cursor: pointer;
    padding-right: 20px;
    appearance: none;
    -webkit-appearance: none;

    option {
      color: #1a1a1a;
      background: #ffffff;
      font-size: 16px !important;
    }
  }

  .select-arrow {
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(320px, 1fr)
  ); /* 適度加寬以符合比例 */
  gap: 24px;
  margin-bottom: 40px;
}

.course-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: none; /* 無陰影 */
  border: none; /* 無外框 */
  transition: transform 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.course-card:hover {
  transform: translateY(-5px);
}

.course-image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 373.33 / 210; /* CSS比例設定 */
  overflow: hidden;
  background-color: #f0f0f0;
  border-radius: 8px;
}

.status-tag {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 4px 12px;
  border-radius: 8px 0 8px 0;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;

  &.not_started {
    color: #1341ab;
    background-color: #e2f2ff;
  }
  &.open {
    color: #ffffff;
    background-color: #1360ca;
  }
  &.closed,
  &.ended {
    color: #6c6c6c;
    background-color: #d8d8d8;
  }
}

.course-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center; /* 置中裁剪 */
}

.course-image-placeholder {
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.course-info {
  padding: 16px 0; /* 調整 padding，因為沒有外框，兩側可以不留白或是根據設計微調，這裡先維持原本或稍減 */
  display: flex;
  flex-direction: column;
  flex: 1;
}

.course-title-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.course-title {
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  line-clamp: 2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.favorite-icon-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
}

.favorite-icon-button:hover {
  background: rgba(17, 113, 221, 0.08);
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: auto;
}

.tag-item {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #f2f2f2; /* 課程 tag 為 #F2F2F2 */
  color: #333;
}

.course-teacher {
  margin-top: 12px;
  font-size: 14px;
  color: #000; /* 講師名稱為 #000 */
  /* 移除原先的 border-top: 1px solid #eee; 以及 padding-top 實現無水平線分隔 */
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}
</style>
